/* eslint-disable react/jsx-no-useless-fragment */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { array, number, object, string } from 'yup';

import { DropdownSingle } from 'src/app/components/lib/dropdown';
import { DropdownMultiple } from 'src/app/components/lib/dropdown/dropdown-multiple';
import { FormSection } from 'src/app/components/lib/form/form';
import { Input, InputAdornment } from 'src/app/components/lib/input';
import { TimeSelect } from 'src/app/components/lib/time-select';
import { eventTypeOptions } from 'src/app/pages/marketing/campaigns/marketing-campaign-builder/automated-campaign-slides/event-type-select/event-type-options';
import {
  convertMinutesToUnit,
  convertUnitToMinutes,
  EVENT_TYPE_IDS,
  EVENT_TYPE_TIME_OF_DAY_ENABLED_IDS,
  EVENT_TYPE_WHEN_TO_SEND_ENABLED_IDS,
  TimeUnitEnum,
} from 'src/app/pages/marketing/campaigns/marketing-campaign-builder/helpers';
import { SummarySidebarSection } from 'src/app/pages/marketing/workflows/workflow-campaign-builder/summary-sidebar/styles';
import { validateWithSchema } from 'src/app/pages/marketing/workflows/workflow-campaign-builder/wizard/helpers';
import { enforceWholeNumber } from 'src/app/pages/settings/devices/registers/utils';
import { useGetLoyaltyDetails } from 'src/app/queries/loyalty/get-details';
import { useGetSegmentsQuery } from 'src/app/queries/segment/get-segments';

import { useWorkflowBuilderContext } from '../../wizard/builder-context';

export const triggerSchema = object({
  eventTypeId: number().required().label('Event Type'),
  recipientSegmentIds: array()
    .when('eventTypeId', {
      is: EVENT_TYPE_IDS.ENTERED_SEGMENT,
      then: array().required().min(1),
      otherwise: array().notRequired(),
    })
    .label('Segments'),
  delayInMinutes: number()
    .when('eventTypeId', {
      is: EVENT_TYPE_IDS.UPCOMING_BIRTHDAY,
      then: number().required(),
      otherwise: number().nullable().notRequired(),
    })
    .label('When to send'),
  delayUnitId: number()
    .when('eventTypeId', {
      is: EVENT_TYPE_IDS.UPCOMING_BIRTHDAY,
      then: number().required().min(1),
      otherwise: number().nullable().notRequired(),
    })
    .label('Delay Unit'),
  timeOfDayToSend: string()
    .when('eventTypeId', {
      is: (eventTypeId: number) => EVENT_TYPE_TIME_OF_DAY_ENABLED_IDS.includes(eventTypeId),
      then: string().nullable().required(),
      otherwise: string().nullable().notRequired(),
    })
    .label('Time of day to send'),
});

export function TriggerSection() {
  const [builderState, dispatch] = useWorkflowBuilderContext();
  const [displayValue, setDisplayValue] = useState('');
  const { data: loyaltyDetails } = useGetLoyaltyDetails();

  const { eventTypeId, recipientSegmentIds, delayInMinutes, delayUnitId, timeOfDayToSend, selectedNodeIsNew } =
    builderState;
  const { data: segments = [], isLoading: segmentsIsLoading } = useGetSegmentsQuery();
  const segmentOptions = useMemo(
    () => segments.map((segment) => ({ label: segment.Name, id: segment.SegmentId })),
    [segments]
  );

  const isEnteredSegmentCampaign = builderState.eventTypeId === EVENT_TYPE_IDS.ENTERED_SEGMENT;
  const selectedTrigger = eventTypeOptions.find((option) => option.id === eventTypeId);
  const whenToSendDescription = selectedTrigger?.whenToSendDescription;

  const setSelectedSegments = useCallback(
    (segments: number[]) => dispatch({ type: 'set-value', payload: { key: 'recipientSegmentIds', value: segments } }),
    [dispatch]
  );
  const setDelayUnit = useCallback(
    (name: number | null) => dispatch({ type: 'set-value', payload: { key: 'delayUnitId', value: name } }),
    [dispatch]
  );
  const setTimeOfDayToSend = useCallback(
    (time: string | null) => dispatch({ type: 'set-value', payload: { key: 'timeOfDayToSend', value: time } }),
    [dispatch]
  );
  const setDelayInMinutes = useCallback(
    (name: number | null) => dispatch({ type: 'set-value', payload: { key: 'delayInMinutes', value: name } }),
    [dispatch]
  );
  const setSelectedEventType = useCallback(
    (eventTypeId: number) => dispatch({ type: 'set-value', payload: { key: 'eventTypeId', value: eventTypeId } }),
    [dispatch]
  );
  const setSendToAll = useCallback(
    (sendToAll: boolean) => dispatch({ type: 'set-value', payload: { key: 'sendToAllCustomers', value: sendToAll } }),
    [dispatch]
  );

  const validationErrors = useMemo(
    () => validateWithSchema(triggerSchema, builderState, selectedNodeIsNew),
    [builderState, selectedNodeIsNew]
  );

  useEffect(() => {
    if (delayInMinutes !== null && delayUnitId !== null) {
      const minutes = Number(delayInMinutes);
      if (!Number.isNaN(minutes)) {
        const convertedValue = convertMinutesToUnit(minutes, delayUnitId);
        setDisplayValue(convertedValue.toString());
      }
    } else {
      setDisplayValue('');
      setDelayInMinutes(null);
    }
  }, [delayInMinutes, delayUnitId, setDisplayValue, setDelayInMinutes]);

  const handleTimeDelayChange = (inputValue: string) => {
    setDisplayValue(inputValue);

    if (inputValue !== '' && delayUnitId) {
      const unitId = Number(delayUnitId);
      const numericValue = Number(inputValue);
      const minutes = convertUnitToMinutes(numericValue, unitId);
      setDelayInMinutes(minutes);
    } else {
      setDelayInMinutes(null);
    }
  };

  const handleEventTypeChange = (eventTypeId: number) => {
    if (eventTypeId === EVENT_TYPE_IDS.ENTERED_SEGMENT) {
      setSendToAll(false);
    } else {
      setSendToAll(true);
    }

    if (eventTypeId === EVENT_TYPE_IDS.UPCOMING_BIRTHDAY) {
      setDelayUnit(TimeUnitEnum.days);
    }

    setSelectedSegments([]);
    setDelayInMinutes(null);
    setTimeOfDayToSend(null);
    setSelectedEventType(eventTypeId);
  };

  const filteredEventTypeOptions = eventTypeOptions.filter((option) => {
    if (option.id === EVENT_TYPE_IDS.LOYALTY_ENROLLMENT) {
      return loyaltyDetails?.LoyaltyRequireOptIn === true;
    }
    return option.isEnabled;
  });

  return (
    <SummarySidebarSection>
      <FormSection>
        <DropdownSingle
          automationId='campaign-workflow-builder-event-type-select'
          disabled={builderState.isReadOnly}
          helpText='Select an event'
          label='Event:'
          labelPlacement='top'
          options={filteredEventTypeOptions.map((option) => ({
            label: option.title,
            id: option.id,
          }))}
          required
          setValue={(option) => handleEventTypeChange(Number(option.id))}
          value={eventTypeId}
        />
        {isEnteredSegmentCampaign && (
          <>
            <DropdownMultiple
              automationId='campaign-workflow-builder-segment-select'
              disabled={builderState.isReadOnly}
              errorMessage={validationErrors.getError('recipientSegmentIds')?.message}
              helpText='Select a segment'
              label='Segments:'
              labelPlacement='top'
              loading={segmentsIsLoading}
              options={segmentOptions}
              required
              setValue={(options) => setSelectedSegments(options.map((option) => Number(option.id)))}
              value={recipientSegmentIds.map((segmentId) => ({
                label: segmentOptions.find((option) => option.id === segmentId)?.label ?? '',
                id: segmentId,
              }))}
            />
          </>
        )}
        {EVENT_TYPE_WHEN_TO_SEND_ENABLED_IDS.includes(eventTypeId ?? 0) && (
          <Input
            automationId='campaign-workflow-builder-time-delay-input'
            disabled={builderState.isReadOnly}
            endAdornment={<InputAdornment position='end'>days</InputAdornment>}
            error={!!validationErrors.getError('delayInMinutes')}
            label='When to send:' // how many days before birthday - this uses the time delay value
            labelPlacement='top'
            required
            tooltip={whenToSendDescription}
            type='number'
            value={displayValue}
            onChange={({ target }) => handleTimeDelayChange(enforceWholeNumber(target.value))}
          />
        )}
        {eventTypeId && EVENT_TYPE_TIME_OF_DAY_ENABLED_IDS.includes(eventTypeId) && (
          <TimeSelect
            automationId='campaign-workflow-builder-time-of-day-select'
            disabled={builderState.isReadOnly}
            errorMessage={validationErrors.getError('timeOfDayToSend')?.message}
            hoursOnly
            label='Time of day to send:'
            labelPlacement='top'
            placeholder='Select a time'
            required
            selectedValue={timeOfDayToSend}
            onChange={(e) => setTimeOfDayToSend(e)}
          />
        )}
      </FormSection>
    </SummarySidebarSection>
  );
}
